'use client'

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetUnitLoadHeightConsolidatedDataQuery } from '@/redux/slices/preship/preship-api-slice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";

interface ConsolidatedViewProps {
  isOpen: boolean;
  onClose: () => void;
  variant: string;
  activeWeek?: string;
}

const UnitLoadHeightConsolidatedView: React.FC<ConsolidatedViewProps> = ({ isOpen, onClose, variant, activeWeek }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('max');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetUnitLoadHeightConsolidatedDataQuery({ projectId, variant, weekType: activeWeek, }, { refetchOnMountOrArgChange: true });
  console.log(consolidatedData, 'height consolidatedData')
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);
  console.log(consolidatedData, 'consolidatedData')
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div></div>;

  const renderRawDataTable = () => {
    // 1. Group raw data by palletId
    const groupedByPalletId = consolidatedData.rawData.reduce((groups: Record<string, any[]>, row: any) => {
      const palletId = row.palletId || 'unknown';
      if (!groups[palletId]) {
        groups[palletId] = [];
      }
      groups[palletId].push(row);
      return groups;
    }, {});

    // 2. Sort groups by palletCreationDate and then sort each group by lpnIndex
    const sortedGroups = Object.entries(groupedByPalletId)
      // Sort groups by palletCreationDate
      .sort(([, groupA], [, groupB]) => {
        const dateA = new Date(groupA[0]?.palletCreationDate || 0).getTime();
        const dateB = new Date(groupB[0]?.palletCreationDate || 0).getTime();
        return dateA - dateB;
      })
      .map(([, group]) => {
        // Sort each group by lpnIndex
        return group.sort((a, b) => {
          const indexA = parseInt(a.lpnIndex) || 0;
          const indexB = parseInt(b.lpnIndex) || 0;
          return indexA - indexB;
        });
      });

    // 3. Flatten the sorted groups back into a single array
    const sortedData = sortedGroups.flat();

    return (
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet No</TableHead>
            <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Name</TableHead>
            <TableHead rowSpan={2} className="border border-gray-300 text-center  bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
            {(variant === 'postShip' || variant === 'postStack') ? (
              <>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B (in)</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C (in)</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D (in)</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A (in)</TableHead>
              </>
            ) : (
              <>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A (in)</TableHead>
              </>
            )}
          </TableRow>
          {(variant === 'postShip' || variant === 'postStack') && (
            <TableRow>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">{variant === 'postStack' ? 'Post Stack' : 'Post Ship'}</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">{variant === 'postStack' ? 'Post Stack' : 'Post Ship'}</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">{variant === 'postStack' ? 'Post Stack' : 'Post Ship'}</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">{variant === 'postStack' ? 'Post Stack' : 'Post Ship'}</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
            </TableRow>
          )}
        </TableHeader>
        <TableBody>
          {sortedData.map((row: any, index: number) => (
            <TableRow key={`${row.palletId}-${row.lpnIndex || index}`} className={"bg-gray-100"}>
              <TableCell className="border text-center border-gray-300 text-center p-2">{row.lpnIndex || index + 1}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
              {(variant === 'postShip' || variant === 'postStack') ? (
                <>
                  <TableCell className="border border-gray-300 text-center p-2">{row['A-BPreShip']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row[`A-B${variant === 'postShip' ? 'PostShip' : 'PostStack'}`]}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row['A-BStatus'] === 'Pass' ? "bg-states-success text-white" : "bg-bgRed   text-white")}>{row['A-BCalculated']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['B-CPreShip']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row[`B-C${variant === 'postShip' ? 'PostShip' : 'PostStack'}`]}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row['B-CStatus'] === 'Pass' ? "bg-states-success text-white " : "bg-bgRed text-white")}>{row['B-CCalculated']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['C-DPreShip']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row[`C-D${variant === 'postShip' ? 'PostShip' : 'PostStack'}`]}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row['C-DStatus'] === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row['C-DCalculated']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['D-APreShip']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row[`D-A${variant === 'postShip' ? 'PostShip' : 'PostStack'}`]}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row['D-AStatus'] === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row['D-ACalculated']}</TableCell>
                </>
              ) : (
                <>
                  <TableCell className="border border-gray-300 text-center p-2">{row['A-B']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['B-C']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['C-D']}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row['D-A']}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderStatisticsTable = () => (
    <div className="space-y-6">
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Statistic</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A (in)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {['Max', 'Min', 'Average', 'stdev', 'Count'].map((stat, index) => (
            <React.Fragment key={stat}>
              {consolidatedData.statistics[stat.toLowerCase()].map((row: any, rowIndex: number) => (
                <TableRow key={`${stat}-${rowIndex}`} className={"bg-gray-100"}>
                  {rowIndex === 0 && (
                    <TableCell rowSpan={consolidatedData.statistics[stat.toLowerCase()].length} className="border border-gray-300 text-center p-2 font-semibold">
                      {stat}
                    </TableCell>
                  )}
                  <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant === 'postStack') && stat !== 'Count' && parseFloat(row['A-B']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['A-B']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant === 'postStack') && stat !== 'Count' && parseFloat(row['B-C']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['B-C']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant === 'postStack') && stat !== 'Count' && parseFloat(row['C-D']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['C-D']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant === 'postStack') && stat !== 'Count' && parseFloat(row['D-A']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['D-A']}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Data Fill Status</TableHead>
            {(variant === 'postShip' || variant === 'postStack') && <>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Overall pass/fail status</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A Pass Count</TableHead>
            </>
            }
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
              {(variant === 'postShip' || variant === 'postStack') && <>
                <TableCell className={`${row.overallStatus === 'Fail' ? "text-white bg-bgRed" : row.overallStatus === 'Pass' ? " text-white bg-states-success" : ""} border border-gray-300 text-center p-2`}>{row.overallStatus}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['A-BFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['A-BFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['A-BPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white  ")}>{row['A-BPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['B-CFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white  ")}>{row['B-CFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['B-CPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white    ")}>{row['B-CPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['C-DFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['C-DFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['C-DPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white    ")}>{row['C-DPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['D-AFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['D-AFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['D-APassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row['D-APassCount']}</TableCell>
              </>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletType,
      'A-B': parseFloat(item['A-B']),
      'B-C': parseFloat(item['B-C']),
      'C-D': parseFloat(item['C-D']),
      'D-A': parseFloat(item['D-A']),
    }));

    const colors = {
      'A-B': "#004899",
      'B-C': "#82ca9d",
      'C-D': "#ffc658",
      'D-A': "#ff7300"
    };

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['max', 'min', 'average', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start hover:bg-blue hover:text-white",
                activeStatistic === stat ? "bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="A-B" fill={colors['A-B']} />
            <Bar barSize={20} dataKey="B-C" fill={colors['B-C']} />
            <Bar barSize={20} dataKey="C-D" fill={colors['C-D']} />
            <Bar barSize={20} dataKey="D-A" fill={colors['D-A']} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Unit Load Height Consolidated View {`(${(variant === 'postShip') ? "Post Ship" : variant === 'preShip' ? "Pre Ship" : "Post Stack"})`}</DialogTitle>
        </DialogHeader>
        <div className="flex h-[calc(100vh-200px)]">
          <div className="w-48 pt-10 bg-bgBlue">
            <div className="">
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'raw' ? " bg-blue text-white" : " text-gray-700 bg-bgBlue"
                )}
                onClick={() => setActiveTab('raw')}
              >
                Raw Data
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'statistics' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('statistics')}
              >
                Statistics
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'graphs' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('graphs')}
              >
                Graphs
              </Button>
            </div>
          </div>
          <div className="flex-1 p-6 overflow-auto">
            {activeTab === 'raw' && renderRawDataTable()}
            {activeTab === 'statistics' && renderStatisticsTable()}
            {activeTab === 'graphs' && renderGraph()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnitLoadHeightConsolidatedView;
